import StateObserver, { now, user } from 'src/StateObserver';
import {
  closePopup,
  openPopupPromise,
  type PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import { track } from 'src/lib/analytics/utils';
import { getResubscribePopupViewCount } from 'src/replicant/chatbot/chatbot.getters';
import {
  GCInstant,
  getFriendshipAsync,
  isLiffFriendshipApiSupported,
  requestFriendshipAsync,
} from 'src/lib/gcinstant';
import { startNativeCrossplay } from 'src/game/crossplay/crossplay.sequence';
import { openNativeAppStorePage } from 'src/game/native/native.util';
import { updatePrivateProfile } from 'src/lib/profile';
import { captureGenericError } from 'src/lib/sentry';
import { waitForItPromise, withLoading } from 'src/lib/utils';
import { duration } from 'src/replicant/utils/duration';
import { analytics, PlatformWeb } from '@play-co/gcinstant';
import {
  RESUBSCRIBE_HINT_KEYS,
  type ResubscribeHintKey,
} from './PopupResubscribeHintOverlay';

function isUserInputError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === 'USER_INPUT'
  );
}

function captureReportableError(message: string, error: unknown): void {
  if (!isUserInputError(error)) {
    captureGenericError(message, error);
  }
}

/**
 * Request OA friendship via LIFF SDK and verify the result, wrapped in a loading spinner.
 * @returns `true` if the user added the OA as friend, `false` otherwise.
 */
export async function requestAndVerifyChatbotSubscription(): Promise<boolean> {
  if (!isLiffFriendshipApiSupported()) {
    return false;
  }

  try {
    return await withLoading(async () => {
      await requestFriendshipAsync();
      const isFriend = await getFriendshipAsync();
      // Capture must run after getFriendship() resolves (the profile grant
      // happens in its popover). Fire-and-forget so the friend flag returns
      // without waiting on the extra profile fetch — it's best-effort anyway.
      void captureLineProfileAfterConsent();
      return isFriend;
    });
  } catch (error) {
    captureReportableError('resubscribe request friendship failed', error);
    return false;
  }
}

/**
 * Uniformly random-picks one of the hint copies shown in the resubscribe
 * hint overlay. Spec allows either sequential or random rotation per exposure;
 * random is chosen to avoid a new replicant state field.
 */
function pickRandomHint(): ResubscribeHintKey {
  const index = Math.floor(Math.random() * RESUBSCRIBE_HINT_KEYS.length);
  return RESUBSCRIBE_HINT_KEYS[index]!;
}

/**
 * Capture the LINE display name/photo after the friendship flow, but only when
 * the `profile` scope is already granted.
 *
 * `getFriendshipAsync()` above requires the scope and may itself trigger LINE's
 * consent popover, yet it only returns the friend flag — never the name/photo.
 * We read those via `getProfileAsync()`, but gate on `liff.permission.query`
 * first: calling `getProfileAsync()` without the grant would show a SECOND
 * consent popover. When already granted it resolves silently. No-op (never throws)
 * otherwise, so the friendship flow's behavior is unchanged.
 */
async function captureLineProfileAfterConsent(): Promise<void> {
  if (!(GCInstant instanceof PlatformWeb)) return;

  try {
    // Only read the profile when the scope is granted, to avoid re-prompting.
    // Optional-chain the whole call so an older LIFF SDK without the permission
    // API degrades to a no-op instead of throwing inside the friendship flow.
    const permission = await window.liff?.permission?.query?.('profile');
    if (permission?.state !== 'granted') return;

    const profile = await GCInstant.getProfileAsync();
    if (!profile?.playerName) return;

    // Update the instance so the new name/photo are available immediately.
    GCInstant.playerName = profile.playerName;
    if (profile.playerPhoto) {
      GCInstant.playerPhoto = profile.playerPhoto;
    }

    // Persist the captured profile to the server.
    updatePrivateProfile();
  } catch (e) {
    // Best-effort capture — a LIFF/network hiccup must not break the
    // friendship flow. The editor keeps its existing fallback name/avatar.
    // Report it so we can see if the grant-then-fetch path ever fails.
    captureGenericError('captureLineProfileAfterConsent', e);
  }
}

/**
 * Opens the hint overlay popup, awaits the LIFF friendship flow, then closes
 * the overlay. Replaces the default `withLoading` global spinner for this path
 * — the popup provides its own dim background and spinner.
 */
async function requestAndVerifyWithHintOverlay(
  hintID: ResubscribeHintKey,
): Promise<boolean> {
  const openPromise = openPopupPromise('popupResubscribeHintOverlay', {
    hintID,
  });

  try {
    if (!isLiffFriendshipApiSupported()) {
      return false;
    }

    await requestFriendshipAsync();
    const isFriend = await getFriendshipAsync();
    // Capture must run after getFriendship() resolves (the profile grant
    // happens in its popover). Fire-and-forget so the friend flag returns
    // without waiting on the extra profile fetch — it's best-effort anyway.
    void captureLineProfileAfterConsent();
    return isFriend;
  } catch (error) {
    captureReportableError('resubscribe direct overlay failed', error);
    return false;
  } finally {
    closePopup('popupResubscribeHintOverlay');
    await openPromise;
  }
}

/**
 * Legacy subscribeBotAsync flow: call the API, wait, and reload the page.
 * Wrapped in withLoading to block UI and prevent further logic execution.
 */
async function legacySubscribeAndReload() {
  await withLoading(async () => {
    await GCInstant.subscribeBotAsync();
    await analytics.flush();
    await waitForItPromise(duration({ seconds: 5 }));
    window.location.reload();
  });
}

/**
 * Soft resubscribe prompt sequence.
 *
 * Shown on every eligible launch to unsubscribed users. Offers a 50-gem
 * reward on first subscribe. Handles: popup display, analytics, reward
 * claim, and reward popup.
 */
export async function resubscribePromptSequence(origin: PopupDialogOrigin) {
  await StateObserver.invoke.triggerResubscriptionPromptCooldown();
  await StateObserver.invoke.incrementResubscribePopupViewCount();
  const allTimeViewCount = getResubscribePopupViewCount(user());

  track('ResubscribePopupOpen', {
    popupType: 'prompt',
    allTimeViewCount,
    isHintShown: false,
  });

  const result = await openPopupPromise('popupResubscribePrompt', {
    origin,
  });

  track('ResubscribePopupClose', {
    popupType: 'prompt',
    closeReason: result,
    allTimeViewCount,
    isHintShown: false,
  });

  if (result === 'subscribe') {
    // The prompt shows on every eligible launch. If the user previously
    // subscribed and already has a resubscriptionState, skip the intent and
    // reward to avoid throwing on duplicate calls.
    const skipIntentAndReward = !!user().chatbot.resubscriptionState;

    if (!skipIntentAndReward) {
      await StateObserver.invoke.triggerResubscriptionIntent();
    }

    if (!isLiffFriendshipApiSupported()) {
      await legacySubscribeAndReload();
      return;
    }

    if (!skipIntentAndReward) {
      await StateObserver.invoke.claimResubscriptionReward();
      await openPopupPromise('popupResubscribeReward', { origin });
    }
  }
}

/**
 * Force resubscribe prompt sequence.
 *
 * Legacy variant kept for compatibility. No longer invoked after A/B 0455
 * resolved to 'enabled' (soft prompt on every launch). Retained so the
 * popup and its assets remain reachable if we need to re-enable it.
 */
export async function resubscribeForceSequence(origin: PopupDialogOrigin) {
  await StateObserver.invoke.incrementResubscribePopupViewCount();
  const allTimeViewCount = getResubscribePopupViewCount(user());

  track('ResubscribePopupOpen', {
    popupType: 'force',
    allTimeViewCount,
    isHintShown: false,
  });

  const result = await openPopupPromise('popupResubscribeForce', {
    origin,
  });

  track('ResubscribePopupClose', {
    popupType: 'force',
    closeReason: result,
    allTimeViewCount,
    isHintShown: false,
  });

  if (result === 'subscribe') {
    if (!isLiffFriendshipApiSupported()) {
      await legacySubscribeAndReload();
    }
  }

  if (result === 'play_on_app') {
    const timestamp = now();
    const tracking = {
      feature: 'force_resubscribe_popup' as const,
      subFeature: 'force_resubscribe_popup_cta' as const,
      timestamp,
      origin,
    };

    const appOpened = await startNativeCrossplay({
      payload: GCInstant.entryData,
      tracking,
      shouldPreserveChannel: true,
    });

    if (!appOpened) {
      openNativeAppStorePage({
        payload: GCInstant.entryData,
        tracking,
      });
    }

    // Block UI indefinitely so the user can't bypass the force popup
    // by returning from the app store without installing.
    await withLoading(() => new Promise(() => {}));
  }
}

/**
 * Direct LINE overlay sequence.
 *
 * For unsubscribed users on eligible launches: skip the in-game popup and
 * open LINE's requestFriendship() overlay immediately. No gem reward is
 * granted in this flow. Falls back to resubscribePromptSequence when LIFF
 * is unavailable.
 */
export async function resubscribeDirectOverlaySequence(
  origin: PopupDialogOrigin,
) {
  if (!isLiffFriendshipApiSupported()) {
    // No LIFF overlay available — fall back to the popup flow so users on
    // older clients still have a resubscribe path.
    await resubscribePromptSequence(origin);
    return;
  }

  await StateObserver.invoke.incrementResubscribePopupViewCount();
  const allTimeViewCount = getResubscribePopupViewCount(user());

  const hintID = pickRandomHint();

  track('ResubscribePopupOpen', {
    popupType: 'direct_overlay',
    allTimeViewCount,
    isHintShown: true,
    hintID,
  });

  const subscribed = await requestAndVerifyWithHintOverlay(hintID);

  track('ResubscribePopupClose', {
    popupType: 'direct_overlay',
    closeReason: subscribed ? 'subscribe' : 'close_button',
    allTimeViewCount,
    isHintShown: true,
    hintID,
  });
}
